import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PlanDto } from '@foco/contracts';

/**
 * Tariff cards.
 *
 * The pricing policy of section 13.2 is applied literally here, and it is worth
 * restating because it looks like a mistake to anyone reading the code:
 *
 *   * the one-off plan is deliberately the **most expensive per request**
 *     ($0.30 against roughly $0.0033 on the monthly plan). That is the
 *     customer's explicit decision: it exists so somebody who needs exactly one
 *     check can buy exactly one check, not to compete with a subscription;
 *   * therefore the one-off plan is **not** marked recommended, and its
 *     per-request cost is shown **next to** the subscription's — the honest way
 *     to make that pricing work, rather than hiding it.
 */

const ORDER: readonly string[] = ['single', 'monthly', 'quarterly', 'yearly'];

function periodLabel(plan: PlanDto, t: (key: string) => string): string {
  if (plan.type === 'one_time' || !plan.periodDays) return t('pricing.oneTime');
  if (plan.periodDays >= 365) return t('pricing.perYear');
  if (plan.periodDays >= 90) return t('pricing.perQuarter');
  return t('pricing.perMonth');
}

/** Cost of a single request on this plan — the number the policy turns on. */
export function pricePerRequest(plan: PlanDto): number | null {
  if (plan.requestsLimit <= 0 || plan.priceUsd <= 0) return null;
  return plan.priceUsd / plan.requestsLimit;
}

/**
 * The tariff name.
 *
 * The names are configuration data (`app.config.yaml`) and therefore may not be
 * translated for every locale. When a name is missing the id is shown, never the
 * raw i18n key — which is exactly the bug this replaced: `plans.<id>` is an
 * *object* (`{ name, tagline, feature1… }`) and asking i18next for it produced
 * `key 'plans.single (ru)' returned an object instead of string`.
 */
export function planName(
  t: (key: string, options?: Record<string, unknown>) => string,
  planId: string,
): string {
  return t(`plans.${planId}.name`, { defaultValue: planId });
}

/**
 * Plan bullets.
 *
 * The *quantities* are read from the plan the API returns rather than baked into
 * the translation strings, because section 13.2 requires prices and limits to be
 * changeable in `app.config.yaml` alone. A bullet that said "1,500 requests" in
 * four catalogues would keep saying it after the config was changed to 2,000 —
 * and a pricing page that disagrees with what the customer is charged is worse
 * than an untranslated one.
 *
 * The *capabilities* (full card, risk explanation, export, support) are product
 * copy and stay in the catalogues.
 */
export function planFeatures(
  t: (key: string, options?: Record<string, unknown>) => string,
  plan: PlanDto,
): string[] {
  const features: string[] = [];

  // What the plan includes, stated with the live numbers.
  if (plan.requestsLimit > 0) {
    features.push(t('plans.featureRequests', { count: plan.requestsLimit }));
  } else {
    features.push(t('plans.requestsFree'));
  }
  if (plan.seatsIncluded > 0) {
    features.push(t('plans.featureSeats', { count: plan.seatsIncluded }));
  }
  features.push(t('pricing.fullCard'));
  features.push(t('pricing.riskExplanation'));
  if (plan.apiAccess) {
    features.push(
      plan.apiRateLimitRpm === null
        ? t('plans.apiAccess')
        : // i18next selects the plural form from `count`, and the noun that
          // varies is the key count, so that is what is passed as `count`.
          t('plans.featureApi', { count: plan.apiKeysMax ?? 0, rpm: plan.apiRateLimitRpm }),
    );
  } else {
    features.push(t('pricing.noApi'));
  }
  // Export needs a real quota behind it; the one-off card has nothing to export.
  if (plan.requestsLimit >= 100) features.push(t('pricing.export'));
  if (plan.periodDays !== null && plan.periodDays >= 90) features.push(t('pricing.support'));

  return features;
}

/**
 * USD, formatted for the active locale.
 *
 * `fractionDigits` is a *caller* decision, not a function of the value: a plan
 * price is money and reads as `$0.30`, while a cost-per-request is fractions of
 * a cent and needs `$0.0033` or it would round to `$0.00` and destroy the
 * comparison the pricing policy turns on. Deriving it from the value produced
 * `$0.3000` on the one-off plan, which is not how a price is written.
 */
export function formatUsd(value: number, locale: string, fractionDigits = 2): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** The per-request figure needs four decimals; a plan price needs two. */
const PER_REQUEST_DIGITS = 4;

export function PlanCards({ plans }: { plans: readonly PlanDto[] }): React.ReactNode {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const paid = ORDER.map((id) => plans.find((plan) => plan.id === id)).filter(
    (plan): plan is PlanDto => Boolean(plan),
  );
  const monthly = plans.find((plan) => plan.id === 'monthly');
  const monthlyPerRequest = monthly ? pricePerRequest(monthly) : null;

  return (
    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {paid.map((plan) => {
        const perRequest = pricePerRequest(plan);
        const isOneOff = plan.type === 'one_time';

        return (
          <article
            key={plan.id}
            className={[
              'flex flex-col rounded-[var(--radius-card)] border bg-[var(--color-surface)] p-5',
              plan.isRecommended
                ? 'border-brand-600 shadow-[var(--shadow-pop)]'
                : 'border-[var(--color-border)] shadow-[var(--shadow-card)]',
            ].join(' ')}
          >
            <header>
              <h3 className="font-semibold">{planName(t, plan.id)}</h3>
              <p className="mt-2 text-2xl font-bold">
                {formatUsd(plan.priceUsd, locale)}
                <span className="ml-1 text-sm font-normal text-[var(--color-muted-fg)]">
                  {periodLabel(plan, t)}
                </span>
              </p>
            </header>

            <p className="mt-3 text-sm text-[var(--color-muted-fg)]">
              {plan.requestsLimit > 0
                ? t('plans.requests', { count: plan.requestsLimit })
                : t('plans.requestsFree')}
            </p>
            <p className="mt-1 text-sm text-[var(--color-muted-fg)]">
              {t('plans.seats', { count: plan.seatsIncluded })}
            </p>

            {/* The one-off plan's real cost, stated rather than buried. */}
            {perRequest !== null && (
              <p className="mt-2 text-sm text-[var(--color-muted-fg)]">
                {formatUsd(perRequest, locale, PER_REQUEST_DIGITS)} {t('pricing.perRequestShort')}
                {isOneOff && monthlyPerRequest !== null && (
                  <>
                    {' · '}
                    <span className="font-medium text-[var(--color-fg)]">
                      {t('pricing.comparisonNote', {
                        times: Math.round(perRequest / monthlyPerRequest),
                      })}
                    </span>
                  </>
                )}
              </p>
            )}

            <ul className="mt-4 flex-1 space-y-1.5 text-sm">
              {planFeatures(t, plan).map((feature, index) => (
                <Feature key={`${plan.id}-${index}`} ok>
                  {feature}
                </Feature>
              ))}
            </ul>

            <Link
              to={`/signup/${plan.id}`}
              className={[
                'mt-5 rounded-[var(--radius-control)] px-4 py-2.5 text-center text-sm font-semibold transition',
                plan.isRecommended
                  ? 'bg-brand-700 text-white hover:bg-brand-800'
                  : 'border border-[var(--color-border)] hover:border-brand-600 hover:text-brand-600',
              ].join(' ')}
            >
              {t('plans.select')}
            </Link>

            {/* Why this plan exists (section 13.2). The free tariff has no
                price discussion; the one-off tariff has the honest note. */}
            <p className="mt-3 text-xs text-[var(--color-muted-fg)]">
              {t(`plans.${plan.id}.tagline`, { defaultValue: '' })}
            </p>
          </article>
        );
      })}
    </div>
  );
}

function Feature({ ok, children }: { ok: boolean; children: React.ReactNode }): React.ReactNode {
  return (
    <li className={ok ? 'text-[var(--color-fg)]' : 'text-[var(--color-muted-fg)] line-through'}>
      <span aria-hidden className="mr-1.5">
        {ok ? '✓' : '—'}
      </span>
      {children}
    </li>
  );
}
